import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError, downloadCsv } from "@/lib/api";
import { useOutletContext } from "react-router-dom";
import { PageHeader, StatusBadge } from "@/components/Shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Loader2, Check, X, CalendarDays, Palmtree, Download, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const TYPE_COLORS = {
  Casual: "bg-blue-500",
  Sick: "bg-rose-500",
  Earned: "bg-emerald-500",
  "Loss of Pay": "bg-amber-500",
};

function LeaveCalendar({ leaves }) {
  const [cursor, setCursor] = useState(() => new Date().toISOString().slice(0, 7));
  const [y, m] = cursor.split("-").map(Number);
  const startPad = new Date(y, m - 1, 1).getDay();
  const daysInMonth = new Date(y, m, 0).getDate();
  const cells = [];
  for (let i = 0; i < startPad; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const active = leaves.filter((l) => l.status !== "Rejected");
  const onDay = (d) => {
    const ds = `${cursor}-${String(d).padStart(2, "0")}`;
    return active.filter((l) => l.start_date <= ds && ds <= l.end_date);
  };
  const shift = (n) => {
    const dt = new Date(y, m - 1 + n, 1);
    setCursor(dt.toISOString().slice(0, 7));
  };

  return (
    <Card className="p-5 border border-border rounded-xl" data-testid="leave-calendar">
      <div className="flex items-center justify-between mb-4">
        <Button variant="ghost" size="icon" onClick={() => shift(-1)} data-testid="calendar-prev"><ChevronLeft className="w-4 h-4" /></Button>
        <h3 className="font-heading font-bold">
          {new Date(y, m - 1, 1).toLocaleDateString("en-IN", { month: "long", year: "numeric" })}
        </h3>
        <Button variant="ghost" size="icon" onClick={() => shift(1)} data-testid="calendar-next"><ChevronRight className="w-4 h-4" /></Button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div key={d} className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground py-1">{d}</div>
        ))}
        {cells.map((d, i) => (
          <div key={i} className={`min-h-[64px] rounded-lg border p-1 text-left ${d ? "border-border bg-card" : "border-transparent"}`}>
            {d && (
              <>
                <span className="text-xs font-medium">{d}</span>
                <div className="flex flex-wrap gap-0.5 mt-1">
                  {onDay(d).map((l) => (
                    <span
                      key={l.id}
                      title={`${l.employee_name} — ${l.leave_type} (${l.status})`}
                      className={`w-4 h-4 rounded-full flex items-center justify-center text-[8px] font-bold text-white ${TYPE_COLORS[l.leave_type] || "bg-slate-500"} ${l.status === "Pending" ? "opacity-50 ring-1 ring-dashed" : ""}`}
                    >
                      {l.employee_name.split(" ").map((s) => s[0]).slice(0, 2).join("")}
                    </span>
                  ))}
                </div>
              </>
            )}
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-3 mt-4 text-xs text-muted-foreground">
        {Object.entries(TYPE_COLORS).map(([t, c]) => (
          <span key={t} className="flex items-center gap-1.5"><span className={`w-2.5 h-2.5 rounded-full ${c}`} />{t}</span>
        ))}
        <span className="ml-auto italic">Faded = pending approval</span>
      </div>
    </Card>
  );
}

const LEAVE_TYPES = ["Casual", "Sick", "Earned", "Loss of Pay"];
const BAL_TINTS = {
  Casual: "bg-blue-50 text-blue-600 dark:bg-blue-950/50 dark:text-blue-400",
  Sick: "bg-rose-50 text-rose-600 dark:bg-rose-950/50 dark:text-rose-400",
  Earned: "bg-emerald-50 text-emerald-600 dark:bg-emerald-950/50 dark:text-emerald-400",
};

export default function Leaves() {
  const { role } = useOutletContext();
  const qc = useQueryClient();
  const isHR = role === "hr";
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("list");
  const [form, setForm] = useState({ leave_type: "Casual", start_date: "", end_date: "", reason: "" });

  const { data: leaves = [], isLoading } = useQuery({
    queryKey: ["leaves"], queryFn: async () => (await api.get("/leaves")).data,
  });
  const { data: balance = {} } = useQuery({
    queryKey: ["leave-balance"], queryFn: async () => (await api.get("/leaves/balance")).data,
    enabled: !isHR,
  });

  const applyMut = useMutation({
    mutationFn: async () => (await api.post("/leaves", form)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["leaves"] }); setOpen(false); setForm({ leave_type: "Casual", start_date: "", end_date: "", reason: "" }); toast.success("Leave request submitted"); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const decideMut = useMutation({
    mutationFn: async ({ id, status }) => (await api.patch(`/leaves/${id}/decision`, { status })).data,
    onSuccess: (_, v) => { qc.invalidateQueries({ queryKey: ["leaves"] }); qc.invalidateQueries({ queryKey: ["leave-balance"] }); qc.invalidateQueries({ queryKey: ["attendance-logs"] }); toast.success(`Leave ${v.status.toLowerCase()}`); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div>
      <PageHeader title="Leave Management" subtitle={isHR ? "Review and approve employee leave requests." : "Apply for leave and track your balances."}>
        <div className="flex items-center gap-1 rounded-full border border-border p-0.5 bg-secondary" data-testid="leaves-view-toggle">
          <button onClick={() => setView("list")} data-testid="leaves-view-list" className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${view === "list" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>List</button>
          <button onClick={() => setView("calendar")} data-testid="leaves-view-calendar" className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${view === "calendar" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>Calendar</button>
        </div>
        {isHR && (
          <Button variant="outline" size="sm" className="rounded-lg" onClick={() => downloadCsv("/export/leaves.csv", "skanovix_leaves.csv")} data-testid="leaves-export-button">
            <Download className="w-4 h-4 mr-1" /> CSV
          </Button>
        )}
        {!isHR && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="leave-apply-button" className="rounded-lg font-semibold active:scale-[0.98]">
                <Plus className="w-4 h-4 mr-1.5" /> Apply Leave
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle className="font-heading">Apply for Leave</DialogTitle><DialogDescription>Submit a leave request for HR approval.</DialogDescription></DialogHeader>
              <div className="space-y-3 py-2">
                <div className="space-y-1.5"><Label>Leave Type</Label>
                  <Select value={form.leave_type} onValueChange={(v) => set("leave_type", v)}>
                    <SelectTrigger data-testid="leave-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{LEAVE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>From</Label><Input type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} data-testid="leave-start-input" /></div>
                  <div className="space-y-1.5"><Label>To</Label><Input type="date" value={form.end_date} onChange={(e) => set("end_date", e.target.value)} data-testid="leave-end-input" /></div>
                </div>
                <div className="space-y-1.5"><Label>Reason</Label><Textarea value={form.reason} onChange={(e) => set("reason", e.target.value)} placeholder="Reason for leave" data-testid="leave-reason-input" /></div>
              </div>
              <DialogFooter>
                <Button onClick={() => applyMut.mutate()} disabled={applyMut.isPending || !form.start_date || !form.end_date} data-testid="leave-submit-button" className="rounded-lg">
                  {applyMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit Request"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </PageHeader>

      {!isHR && (
        <div className="grid grid-cols-3 gap-4 mb-6">
          {["Casual", "Sick", "Earned"].map((t) => (
            <Card key={t} className="p-5 border border-border rounded-xl" data-testid={`balance-${t}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{t} Leave</p>
                  <p className="font-heading text-3xl font-extrabold mt-1">{balance[t] ?? "—"}</p>
                  <p className="text-[11px] text-muted-foreground">days available</p>
                </div>
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${BAL_TINTS[t]}`}><Palmtree className="w-5 h-5" /></div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {view === "list" ? (
      <Card className="border border-border rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-border flex items-center gap-2"><CalendarDays className="w-4 h-4 text-accent" /><h3 className="font-heading font-semibold">{isHR ? "All Leave Requests" : "My Leave Requests"}</h3></div>
        {isLoading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {isHR && <TableHead>Employee</TableHead>}
                  <TableHead>Type</TableHead><TableHead>From</TableHead><TableHead>To</TableHead>
                  <TableHead className="text-right">Days</TableHead><TableHead>Reason</TableHead>
                  <TableHead>Status</TableHead>{isHR && <TableHead className="text-right">Action</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {leaves.length === 0 && <TableRow><TableCell colSpan={isHR ? 8 : 6} className="text-center text-muted-foreground py-8">No leave requests yet.</TableCell></TableRow>}
                {leaves.map((l) => (
                  <TableRow key={l.id} data-testid={`leave-row-${l.id}`}>
                    {isHR && <TableCell className="font-medium">{l.employee_name}</TableCell>}
                    <TableCell>{l.leave_type}</TableCell>
                    <TableCell className="font-mono text-xs">{l.start_date}</TableCell>
                    <TableCell className="font-mono text-xs">{l.end_date}</TableCell>
                    <TableCell className="text-right font-mono">{l.days}</TableCell>
                    <TableCell className="text-muted-foreground text-xs max-w-[180px] truncate">{l.reason || "—"}</TableCell>
                    <TableCell><StatusBadge status={l.status} /></TableCell>
                    {isHR && (
                      <TableCell className="text-right">
                        {l.status === "Pending" ? (
                          <div className="flex justify-end gap-1.5">
                            <Button size="sm" variant="secondary" className="h-8 rounded-lg" onClick={() => decideMut.mutate({ id: l.id, status: "Approved" })} disabled={decideMut.isPending} data-testid={`approve-leave-${l.id}`}>
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="outline" className="h-8 rounded-lg" onClick={() => decideMut.mutate({ id: l.id, status: "Rejected" })} disabled={decideMut.isPending} data-testid={`reject-leave-${l.id}`}>
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ) : <span className="text-xs text-muted-foreground">—</span>}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
      ) : (
        <LeaveCalendar leaves={leaves} />
      )}
    </div>
  );
}
